import { json } from '@codemirror/lang-json';
import { EditorView } from '@codemirror/view';
import { Autocomplete } from '@mui/joy';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { Text } from '@/design-system';
import { isOpenAIFunctions } from '@/utils/ai-tools';
import { appDatasetsPath, appOrganizationPath } from '@/utils/constants';
import { tryParseJSON } from '@/utils/try-parse-json';
import {
  jsonSchemaExtensions,
  useCodeMirrorTooltipFollowMouse,
} from '@/utils/use-codemirror-tooltip-follow-mouse';

import {
  useDataset,
  useDatasetSplits,
  useOrganizationId,
  useSession,
} from '../../hooks/useSwr';
import {
  DatasetSchema,
  ExampleSchema,
  MessageUnionType,
  RunSchema,
} from '../../types/schema';
import { getDataTypeTitle } from '../../utils/get-data-type-title';
import { Code, CodeCard, CodeText } from '../Code/Code';
import { DatasetPicker } from '../DatasetPicker';
import { EditableChatInput } from '../EditableChatInput';
import { EditableMessage } from '../EditableMessage';
import { Pane } from '../Pane';
import { Switch } from '../Switch/Switch';
import useToast from '../Toast/useToast';
import { EditInputFunctionsList } from './EditInputFunctions';
import { useExampleCrud } from './hooks';
import { getDataType, getDefaultInputs, getDefaultOutputs } from './utils';

const customGutterStyle = EditorView.theme({
  '.cm-gutter': {
    width: '34px',
  },
});

export interface BaseProps {
  run?: RunSchema;
  example?: ExampleSchema;
  dataset?: DatasetSchema;
  onSuccess?: () => void;
  overrideInputs?: string;
  overrideOutputs?: string;
  hideInputsAndOutputs?: boolean;
  color?: 'primary' | 'secondary';
  omitIcon?: boolean;
  trigger?: (onClick: () => void) => React.ReactNode;
}

interface PaneProps extends BaseProps {
  isOpen: boolean;
  doClose: () => void;
  asModal?: boolean;
}

export function EditInputs({
  inputs,
  setInputs,
  dataType,
}: {
  inputs: string;
  setInputs: (value: string) => void;
  dataType: DatasetSchema['data_type'];
}) {
  if (dataType === 'llm') {
    return (
      <CodeCard>
        <CodeText
          value={tryParseJSON(inputs)?.input ?? ''}
          onChange={(value) => {
            setInputs(JSON.stringify({ input: value }, null, 2));
          }}
        />
      </CodeCard>
    );
  }

  if (dataType === 'chat') {
    const inputsJson = tryParseJSON(inputs);
    const inputMessages = inputsJson?.input;
    const inputFunctions = inputsJson?.functions;

    if (inputMessages || inputFunctions) {
      return (
        <div className="flex flex-col">
          <strong className="mb-3 font-medium">Messages</strong>
          <div>
            {inputMessages && (
              <EditableChatInput
                messages={inputMessages}
                setMessages={(input: MessageUnionType[]) => {
                  setInputs(JSON.stringify({ ...inputsJson, input }, null, 2));
                }}
                virtualizeItems={true}
              />
            )}
          </div>

          <hr className="my-6 border-secondary" />

          <strong className="mb-3 font-medium">Functions</strong>
          <EditInputFunctionsList
            value={isOpenAIFunctions(inputFunctions) ? inputFunctions : []}
            onChange={(functions) =>
              setInputs(JSON.stringify({ ...inputsJson, functions }, null, 2))
            }
          />
        </div>
      );
    }
  }

  return (
    <CodeCard>
      <div data-testid="edit-inputs-textbox">
        <Code
          language={json()}
          value={inputs}
          onChange={setInputs}
          extensions={[customGutterStyle]}
        />
      </div>
    </CodeCard>
  );
}

export function EditOutputs({
  outputs,
  setOutputs,
  dataType,
}: {
  outputs: string;
  setOutputs: (value: string) => void;
  dataType: DatasetSchema['data_type'];
}) {
  if (dataType === 'llm') {
    return (
      <CodeCard>
        <CodeText
          value={tryParseJSON(outputs)?.output ?? ''}
          onChange={(value) => {
            setOutputs(JSON.stringify({ output: value }, null, 2));
          }}
        />
      </CodeCard>
    );
  }

  if (dataType === 'chat') {
    const outputMessage = tryParseJSON(outputs)?.output;
    return (
      <EditableMessage
        messageValue={outputMessage ?? { type: 'ai', data: { content: '' } }}
        onChange={(value) => {
          setOutputs(JSON.stringify({ output: value }, null, 2));
        }}
      />
    );
  }

  return (
    <CodeCard>
      <div data-testid="edit-outputs-textbox">
        <Code
          language={json()}
          value={outputs}
          onChange={setOutputs}
          extensions={[customGutterStyle]}
        />
      </div>
    </CodeCard>
  );
}

export function ExampleCrudPane(props: PaneProps) {
  const [isOpenDebounced] = useDebounce(props.isOpen, 600);
  return isOpenDebounced || props.isOpen ? ( // unmount on close pane
    <ExampleCrudPaneInternal {...props} />
  ) : null;
}

function ExampleCrudPaneInternal(props: PaneProps) {
  const datasetDataType = getDataType(props.dataset, props.run);
  const title = props.example
    ? 'Edit Example'
    : `Add Example to ${getDataTypeTitle(datasetDataType)} Dataset`;

  const { createToast } = useToast();
  const session = useSession(props.run?.session_id ?? null);
  const [dataset_id, setDatasetID] = useState<string | undefined>(
    props.run ? undefined : props.dataset?.id ?? props.example?.dataset_id
  );
  const organizationId = useOrganizationId();

  const { error, loading, onSubmit } = useExampleCrud({
    example: props.example,
    datasetDataType,
    handleClose: props.doClose,
    onSuccess: () => {
      createToast({
        title: 'Example added to dataset',
        description: (
          <Link
            to={`/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${dataset_id}`}
            className="underline"
          >
            View the dataset
          </Link>
        ),
      });
      props.onSuccess?.();
    },
  });

  const [inputs, setInputs] = useState<string>(
    props.overrideInputs ??
      getDefaultInputs(
        datasetDataType,
        props.run,
        props.example,
        props.dataset?.inputs_schema_definition
      )
  );

  const [outputs, setOutputs] = useState<string>(
    props.overrideOutputs ??
      getDefaultOutputs(
        datasetDataType,
        props.run,
        props.example,
        props.dataset?.outputs_schema_definition
      )
  );

  const [splits, setSplits] = useState<string[]>(
    props.example ? props.example.metadata?.dataset_split : ['base']
  );

  const {
    inputs_schema_definition: inputSchema,
    outputs_schema_definition: outputSchema,
    data_type,
  } = props.dataset ?? {};

  useEffect(() => {
    if (inputSchema && data_type === 'kv') {
      const defaultInputs = getDefaultInputs(
        datasetDataType,
        props.run,
        props.example,
        inputSchema
      );
      setInputs(JSON.stringify(defaultInputs, null, 2));
    }
    if (outputSchema && data_type === 'kv') {
      const defaultOutputs = getDefaultOutputs(
        datasetDataType,
        props.run,
        props.example,
        outputSchema
      );
      setOutputs(JSON.stringify(defaultOutputs, null, 2));
    }
  }, [
    inputSchema,
    outputSchema,
    data_type,
    datasetDataType,
    props.run,
    props.example,
  ]);

  useEffect(() => {
    if (
      session.data &&
      dataset_id === null &&
      session.data.default_dataset_id
    ) {
      setDatasetID(session.data.default_dataset_id);
    }
  }, [session.data, dataset_id]);

  return (
    <Pane
      open={props.isOpen}
      onClose={props.doClose}
      title={title}
      topBarRightElement={
        <div className="mr-5 flex items-center gap-2">
          <Button
            type={'button'}
            onClick={props.doClose}
            color="neutral"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type={'submit'}
            loading={loading}
            disabled={!dataset_id}
            onClick={() => {
              if (dataset_id) {
                onSubmit({
                  inputs,
                  outputs,
                  dataset_id,
                  source_run_id: props.run?.id,
                  ...(splits ? { split: splits } : {}),
                });
              }
            }}
            color="primary"
          >
            Submit
          </Button>
        </div>
      }
    >
      <ExampleCrudPaneContents
        {...props}
        inputs={inputs}
        setInputs={setInputs}
        dataset_id={dataset_id}
        datasetDataType={datasetDataType}
        setDatasetID={setDatasetID}
        outputs={outputs}
        setOutputs={setOutputs}
        error={error}
        splits={splits}
        setSplits={setSplits}
      />
    </Pane>
  );
}

export function ExampleCrudPaneContents(
  props: PaneProps & {
    inputs: string;
    setInputs: (value: string) => void;
    outputs: string;
    setOutputs: (value: string) => void;
    dataset_id: string | undefined;
    datasetDataType: DatasetSchema['data_type'];
    setDatasetID: (value: string | undefined) => void;
    error: string | null;
    splits: string[];
    setSplits: (value: string[]) => void;
  }
) {
  const splitsSwr = useDatasetSplits('latest', props.dataset_id ?? undefined);
  const splitsList = props.dataset_id ? splitsSwr.data : [];

  const datasetSwr = useDataset(props.dataset_id); // Fetch dataset to get input/output schemas
  const inputs_schema_definition = props.dataset_id
    ? datasetSwr.data?.inputs_schema_definition
    : null;
  const outputs_schema_definition = props.dataset_id
    ? datasetSwr.data?.outputs_schema_definition
    : null;

  const [searchSplitInput, setSearchSplitInput] = useState<string>('');
  const splitsListFiltered =
    searchSplitInput !== ''
      ? splitsList?.filter((split) => split.includes(searchSplitInput))
      : splitsList;

  const inputElementRef = useRef<HTMLDivElement>(null);
  const outputElementRef = useRef<HTMLDivElement>(null);

  useCodeMirrorTooltipFollowMouse(
    inputElementRef,
    outputElementRef,
    props.dataset_id
  );

  const [showInputSchema, setShowInputSchema] = useState(false);
  const [showOutputSchema, setShowOutputSchema] = useState(false);

  return (
    <div className="relative grid grid-cols-[minmax(120px,220px),1fr,1fr] gap-x-8 gap-y-5">
      <div className="flex flex-col items-start gap-1">
        <strong className="font-semibold">Input</strong>
        {props.datasetDataType === 'chat' ? (
          <span className="text-sm text-tertiary">
            Edit inputs, add messages and functions
          </span>
        ) : (
          <span className="text-sm text-tertiary">Edit inputs</span>
        )}
        {inputs_schema_definition && (
          <Switch
            id="show-input-schema-switch"
            checked={showInputSchema}
            onChange={() => setShowInputSchema(!showInputSchema)}
            label="Show input schema"
          />
        )}
      </div>
      <div className={showInputSchema ? 'col-span-1' : 'col-span-2'}>
        {inputs_schema_definition ? (
          <CodeCard>
            <div
              data-testid="edit-inputs-textbox"
              className="relative"
              ref={inputElementRef}
            >
              <Code
                language={json()}
                extensions={jsonSchemaExtensions(inputs_schema_definition)}
                value={props.inputs}
                onChange={props.setInputs}
              />
            </div>
          </CodeCard>
        ) : (
          <EditInputs
            inputs={props.inputs}
            setInputs={props.setInputs}
            dataType={props.datasetDataType}
          />
        )}
      </div>
      {showInputSchema && (
        <div className="col-span-1">
          <CodeCard>
            <Code
              language={json()}
              readOnly={true}
              value={JSON.stringify(inputs_schema_definition, null, 2)}
            />
          </CodeCard>
        </div>
      )}

      <div className="flex flex-col items-start gap-1">
        <strong className="font-semibold">Output</strong>
        <span className="text-sm text-tertiary">Edit outputs</span>
        {outputs_schema_definition && (
          <Switch
            id="show-output-schema-switch"
            checked={showOutputSchema}
            onChange={() => setShowOutputSchema(!showOutputSchema)}
            label="Show output schema"
          />
        )}
      </div>
      <div className={showOutputSchema ? 'col-span-1' : 'col-span-2'}>
        {outputs_schema_definition ? (
          <CodeCard>
            <div
              data-testid="edit-outputs-textbox"
              className="relative"
              ref={outputElementRef}
            >
              <Code
                language={json()}
                extensions={jsonSchemaExtensions(outputs_schema_definition)}
                value={props.outputs}
                onChange={props.setOutputs}
              />
            </div>
          </CodeCard>
        ) : (
          <EditOutputs
            outputs={props.outputs}
            setOutputs={props.setOutputs}
            dataType={props.datasetDataType}
          />
        )}
      </div>
      {showOutputSchema && (
        <div className="col-span-1">
          <CodeCard>
            <Code
              language={json()}
              readOnly={true}
              value={JSON.stringify(outputs_schema_definition, null, 2)}
            />
          </CodeCard>
        </div>
      )}

      <div className="flex flex-col items-start">
        {!props.dataset && <strong className="font-semibold">Dataset</strong>}
      </div>
      <div className="max-w-[800px]">
        {!props.dataset && (
          <DatasetPicker
            label={null}
            filter={{ data_type: [props.datasetDataType, 'kv'] }}
            datasetId={props.dataset_id}
            onChange={props.setDatasetID}
          />
        )}
      </div>
      <div />

      <div className="flex flex-col items-start">
        <Text variant="h2">Dataset Splits</Text>
      </div>
      <div className="col-span-2 flex flex-col gap-3">
        <Autocomplete
          autoFocus={false}
          sx={{
            padding: '3px',

            '& .MuiAutocomplete-clearIndicator': { marginRight: '3px' },
          }}
          multiple
          freeSolo={true}
          data-testid="split-picker"
          placeholder={'Choose split…'}
          onChange={(_, values) => {
            props.setSplits(values);
          }}
          onInputChange={(_, value) => {
            setSearchSplitInput(value);
          }}
          clearOnBlur={false}
          value={Array.isArray(props.splits) ? props.splits : []}
          inputValue={searchSplitInput}
          options={splitsListFiltered ?? []}
        />

        {props.error && (
          <Box marginTop={1}>
            <Typography component="p" color="warning">
              {props.error}
            </Typography>
          </Box>
        )}
      </div>
    </div>
  );
}
